import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MeetingClientDto } from '@interfaces/meetings/responses/meeting-visibility.response';
import { MeetingsClientHttpService } from '@services/http/meetings/meetings-client-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { Subscription, first } from 'rxjs';
import {
  formatarDuracaoDaLigacao,
  mensagemDoErro,
} from '@features/dashboard/meetings/components/meeting-presenter';
import { displayTitle } from '@features/dashboard/meetings/components/meeting-visibility-presenter';

/**
 * Calls da empresa para o usuário de cliente (card 3803, `/dashboard/client-meetings`), só leitura.
 *
 * **Pronta e desligada:** a rota fica atrás do `clientMeetingsGuard` (abre só com o parâmetro "Cliente
 * vê calls" ligado) e não tem item de menu. O backend decide quais calls o cliente vê e entrega o
 * recorte (`MeetingClientDto`); a tela só lista.
 */
@Component({
  selector: 'app-client-meetings',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './client-meetings.component.html',
  styleUrl: './client-meetings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientMeetingsComponent implements OnInit {
  readonly breadcrumbs: MenuItem[] = [{ label: 'Calls', routerLink: '/dashboard/client-meetings' }];
  readonly home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };

  protected readonly meetings = signal<MeetingClientDto[]>([]);
  protected readonly total = signal(0);
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly search = signal('');
  protected readonly firstRow = signal(0);
  protected readonly rows = signal(20);
  protected readonly pageSizeOptions = [10, 20, 50];

  protected readonly title = displayTitle;
  protected readonly duration = formatarDuracaoDaLigacao;

  private page = 1;
  private load?: Subscription;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly clientMeetings: MeetingsClientHttpService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.loadPage();
  }

  protected loadPage(): void {
    this.load?.unsubscribe();
    this.loading.set(true);
    this.error.set(null);
    this.load = this.clientMeetings
      .getMeetings(this.page, this.rows(), this.search())
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: page => {
          this.loading.set(false);
          this.meetings.set(page?.data ?? []);
          this.total.set(page?.total ?? 0);
        },
        error: error => {
          this.loading.set(false);
          this.meetings.set([]);
          this.total.set(0);
          this.error.set(
            mensagemDoErro(error, 'Não foi possível carregar as calls. Use Tentar de novo.')
          );
        },
      });
  }

  protected handleSearch(term: string = ''): void {
    const value = term.trim();
    if (value === this.search()) return;
    this.search.set(value);
    this.page = 1;
    this.firstRow.set(0);
    this.loadPage();
  }

  protected handlePagination({ page = 0, rows = 20 }: PaginatorState): void {
    this.page = page + 1;
    this.rows.set(rows);
    this.firstRow.set(page * rows);
    this.loadPage();
  }

  protected open(meeting: MeetingClientDto): void {
    this.router.navigate(['/dashboard/client-meetings', meeting.id]);
  }
}
